import React, { useEffect, useState } from "react";
import { Badge, Button, Spinner, Tooltip } from "reactstrap";
import {
  VsiH4,
  VsiH5,
  VsiHr,
  VsiMedText,
  VsiSmallText,
  VsiUl
} from "../../styled/App.styled";
import axios from "axios";
import { useVsiAppContext } from "../../stores/VsiApp";
import {
  useDebouncedApiCall,
  useTitleFromComponent,
  useInterval
} from "../../utils/hooks";
import {
  uploadsDefs,
  localUploadsDefs
} from "../../utils/gridColumnDefs/admin/uploads";
import Grid from "../../components/Grid";
import Dropzone from "../../components/Dropzone";
import { useDropzone } from "react-dropzone";
import themeStyles from "../../utils/themeStyles";
import { DeleteAuth, GetAuth, PostAuth } from "../../utils/Verbs";
import swal from "sweetalert";
import dayjs from "dayjs";

const parseCompletedStatuses = [
  "error",
  "success",
  "error-parser",
  "error-device",
  "error-ETL",
  "re-uploaded"
];

const uploadRoute = window.location.hostname === "" ? "" : "";

const visibleStyling = {
  position: "fixed",
  width: "90vw",
  height: "100vh",
  background: "rgba(96, 167, 220, 0.5)",
  zIndex: "5"
};

const hiddenStyling = {
  // 'position': 'absolute',
  width: "85vw",
  height: "10px"
};

const clickStyling = {
  position: "absolute",
  height: "9%",
  top: "265px",
  width: "85vw",
  cursor: "pointer"
};

export default function Uploads({ title }) {
  useTitleFromComponent(title);
  const { theme, client, user, getAccessTokenSilently } = useVsiAppContext();
  let { ref } = useDropzone;

  const [isLoading, setIsLoading] = useState(true);
  const [allUploads, setAllUploads] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [filestoUpload, setFilestoUpload] = useState([]);
  const [drag, setDrag] = useState(false);
  const [rowSelected, setRowSelected] = useState(null);
  const [isTrashTipOpen, setTrashTipOpen] = useState(false);
  const [isCancelTipOpen, setCancelTipOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeInterval, setTimeInterval] = useState(3000);

  const [debouncedCallback] = useDebouncedApiCall(() => {
    fetchData()
      .then((data) => {
        setIsRefreshing(false);
      })
      .catch((err) => {
        console.error(err);
        setIsRefreshing(false);
      });
  }, 500);

  // This is here to keep things dry.  We need to fetch off the hook, and again if the user clicks refresh.
  const fetchData = async () => {
    const token = await getAccessTokenSilently();
    GetAuth(`/api/v6/admin/uploads/${client.clientId}`, token)
      // axios.get(`/api/v6/admin/uploads/${client.clientId}`)
      .then((data) => {
        let updData = data.data.map((datum) => {
          return {
            ...datum,
            uploadedBy: datum.uploadedBy
          };
        });
        setAllUploads(updData);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const getAddtlDetails = async function() {
      try {
        fetchData();
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
      }
    };
    getAddtlDetails();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setDrag(false);
    }, 5000);
  }, [drag]);

  useInterval(debouncedCallback, timeInterval);

  const onRefresh_click = () => {
    if (!isRefreshing) {
      setIsRefreshing(true);
      debouncedCallback();
    }
  };

  const onUploadSubmit = async () => {
    if (filestoUpload.length === 0) {
      // Set no files uploaded alert
    } else {
      setIsLoading(true);
      setIsUploading(true);

      // Changed per Sync Upload refactor in Escharum
      const url = `${uploadRoute}/api/v6/upload/file`;
      const token = await getAccessTokenSilently();
      Array.from(filestoUpload).forEach((file) => {
        file.uploadStatus = "uploading";
        file.isUploading = true;
        const formData = new FormData();
        formData.append("files", file);
        // adding client and email to form-date
        formData.append("client_id", client.clientId);
        formData.append("user_email", user.email);
        const config = {
          headers: {
            "content-type": "multipart/form-data",
            fileName: file.name,
            Authorization: `Bearer ${token}`
          }
        };

        axios.post(url, formData, config).then((data) => {
          file.uploadStatus = "success";
          file.isUploading = false;
          postCallBack();
        });
      });
    }
  };

  const postCallBack = () => {
    let doneUploading =
      Array.from(filestoUpload).filter((f) => f.uploadStatus === "uploading")
        .length === 0;
    if (doneUploading) {
      setFilestoUpload([]);
      setIsUploading(false);
      fetchData().then(console.log).catch(console.error);
    }
  };

  const files = filestoUpload.map((file, index) => (
    <li key={file.path} className="p-1">
      <VsiMedText>
        <VsiSmallText>{index + 1 + ". "}</VsiSmallText>
        {file.path}
        <Badge className={"ms-2"}>
          {(file.size / 1048576).toLocaleString?.("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}{" "}
          MB
        </Badge>
      </VsiMedText>
    </li>
  ));

  const onDragEnter = (e) => {
    let classes = e.target.className.split(" ");
    if (!drag && classes) {
      setDrag(true);
    }
  };

  const onDragLeave = (e) => {
    let classes = e.target.className.split(" ");
    if (classes.includes("dropzone")) {
      setDrag(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    setFilestoUpload(acceptedFiles);
    setDrag(false);
  };

  const onRowSelected = (gridRef) => {
    const selectedRow = gridRef?.current?.api?.getSelectedRows?.()?.[0];
    if (selectedRow) {
      setRowSelected(selectedRow);
    }
  };

  const onUploadDelete = async (e) => {
    e.preventDefault();
    const isDeleteUpload = await swal({
      title: `Delete ${
        rowSelected.collectionDate ?? rowSelected.parsingStatus
      } ${rowSelected.deviceName ?? ""} upload?`,
      text: rowSelected.collectionDate ? "This could take 15-20 seconds." : "",
      icon: "warning",
      buttons: true,
      dangerMode: true
    });
    if (isDeleteUpload) {
      try {
        setIsLoading(true);
        const token = await getAccessTokenSilently();
        await DeleteAuth(`/api/v6/admin/upload/${rowSelected._id}`, token).then(res => {
          if (res.status === 202) {
            const colDate = dayjs(res.data.collectionDate).utc();
            const periodId = colDate.diff(dayjs("01/01/20").utc(), "days") + 1827;

            const formData = new FormData();
            formData.append("client_id", res.data.clientId);
            formData.append("period_id", periodId);
            formData.append("device_name", res.data.deviceName);
            formData.append("device_type_code", res.data.deviceTypeCode);

            PostAuth("/api/v6/upload/delete_device", formData, token)
              .then(async data => {

                await fetchData();
                setIsLoading(false);
              });

          }
        });
      } catch (error) {
        setIsLoading(false);
        await swal({
          title: `Error in deleting the upload.`,
          text: error?.response?.data?.message ?? error?.message ?? "",
          buttons: true
        });
        console.error(error);
      }
    }
  };

  const onCancelBtnClick = () => {
    if (timeInterval) setTimeInterval(null);
    else setTimeInterval(3000);
  };

  const trashIcon = (
    <>
      <Button
        onClick={onUploadDelete}
        id={"upload-trash-btn"}
        disabled={
          isLoading ||
          !rowSelected ||
          !parseCompletedStatuses.includes(rowSelected.parsingStatus)
        }
      >
        <i className="fa fa-trash" aria-hidden="true"></i>
      </Button>
      <Tooltip
        target={"upload-trash-btn"}
        isOpen={isTrashTipOpen}
        placement={"top"}
        toggle={() => setTrashTipOpen(!isTrashTipOpen)}
      >
        {!rowSelected
          ? "Select an upload to delete"
          : !parseCompletedStatuses.includes(rowSelected.parsingStatus)
            ? "Select upload needs to finish parsing. Please wait."
            : "Delete upload."}
      </Tooltip>
      <Button onClick={onCancelBtnClick} id={"upload-cancel-btn"}>
        <i
          className={`fa fa-${Boolean(timeInterval) ? "pause" : "play"}`}
          aria-hidden="true"
        />
      </Button>
      <Tooltip
        target={"upload-cancel-btn"}
        isOpen={isCancelTipOpen}
        placement={"top"}
        toggle={() => setCancelTipOpen(!isCancelTipOpen)}
      >
        {`${
          Boolean(timeInterval) ? "Pause" : "Start"
        } upload result long polling`}
      </Tooltip>
    </>
  );

  return (
    <div
      onDragEnter={(e, position) => onDragEnter(e, position)}
      ref={ref}
      onDragLeave={onDragLeave}
    >
      <VsiH4>Upload Device Collection File</VsiH4>
      <VsiHr />
      <Button
        color="success"
        onClick={onUploadSubmit}
        disabled={isUploading || filestoUpload.length === 0}
      >
        {isUploading ? (
          <Spinner size={"sm"} />
        ) : filestoUpload.length > 1 ? (
          "Upload Devices"
        ) : (
          "Upload Device"
        )}
      </Button>

      <div className={"m-1 p-1"} style={{ zIndex: 2 }}>
        {isUploading ? (
          <div className="d-flex justify-content-center">
            <Spinner style={{ color: themeStyles[theme].textColor }} />
          </div>
        ) : (
          <Dropzone
            style={drag ? visibleStyling : hiddenStyling}
            noClick
            onDrop={onDrop}
          >
            <Dropzone noDrag onDrop={onDrop} style={clickStyling} />
          </Dropzone>
        )}
        <div
          style={{
            marginTop: files.length < 1 ? "140px" : "70px",
            maxHeight: files.length && 140,
            overflowY: "scroll",
            zIndex: files.length && 10
          }}
        >
          <VsiUl>{files}</VsiUl>
        </div>
      </div>
      <div>
        <VsiH5>Uploads in Progress</VsiH5>
        <Grid
          isLoading={isLoading}
          rowSelection={"single"}
          data={allUploads || []}
          columnDefs={
            user.role === "super_user" ? uploadsDefs : localUploadsDefs
          }
          onRowSelected={onRowSelected}
          customToolbarButton={trashIcon}
          isRefreshing={isRefreshing}
          onRefresh_click={onRefresh_click}
          onSelectionChanged={onRowSelected}
          autoSizeStrategy={"fitCellContents"}
        />
      </div>
    </div>
  );
}
